import {
  Camera as CameraIcon,
  Code,
  Image,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/date-utils";
import type { CameraPlan, ZeroDateValue } from "@/types/camera-plans";

function zeroDateToDate(value: ZeroDateValue): Date | string | null {
  if (typeof value === "number") return new Date(value);
  return value;
}

interface CameraPlanCardProps {
  plan: CameraPlan;
  cameraCount: number;
  to: string;
  onDelete: () => void;
}

export function CameraPlanCard({
  plan,
  cameraCount,
  to,
  onDelete,
}: CameraPlanCardProps) {
  return (
    <Card
      className="relative cursor-pointer hover:bg-muted/50 transition-colors"
      data-testid={`camera-plan-card-${plan.id}`}
    >
      <Link
        to={to}
        className="absolute inset-0 z-0"
        aria-label={`Open ${plan.name}`}
      />
      <CardContent className="relative z-10 p-4 pointer-events-none">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
              {plan.mapType === "embed" ? (
                <Code className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Image className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <h4 className="font-medium text-foreground truncate">
                {plan.name}
              </h4>
              {plan.description && (
                <p className="text-sm text-muted-foreground truncate">
                  {plan.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {plan.eventDate && (
              <Badge variant="outline">
                {formatDate(zeroDateToDate(plan.eventDate))}
              </Badge>
            )}
            <Badge variant="secondary" className="gap-1">
              <CameraIcon className="h-3 w-3" />
              {cameraCount}
            </Badge>
            <Badge variant="outline">
              {plan.mapType === "embed" ? "Embed" : "Image"}
            </Badge>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  offlineEdit
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete camera plan?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete "{plan.name}" and all its
                    cameras. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
